import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '@phosphor-icons/react';
import {
  isTaskTypeViewSourcePlacement,
  type TaskTypeView,
  type TaskTypeViewPlacement,
} from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import {
  addViewColumn,
  listColumnOptions,
  listSupplierLineOptions,
  listViewFieldOptions,
  listViewSupplierOptions,
  MAX_VIEW_COLUMNS,
  moveViewColumn,
  moveViewPlacement,
  removeViewColumn,
  removeViewPlacement,
  setViewColumnLabel,
  setViewColumnMode,
  setViewColumnPath,
  setViewColumnTarget,
  setSupplierLineSupplierPath,
  setViewPlacementLabel,
  setViewPlacementMode,
  setViewPlacementPath,
  setViewMatchTarget,
  type ViewFieldOption,
  type ViewSchemaField,
} from './taskTypeViewModel';
import {
  ViewLabelInput,
  ViewMatchTargetToggle,
  ViewModeToggle,
  ViewPathSelect,
  ViewSupplierPathSelect,
} from './TaskTypeViewPathControls';
import { TaskTypeViewSourceRow } from './TaskTypeViewSourceRow';
import { TaskTypeViewSupplierLineColumns } from './TaskTypeViewSupplierLineColumns';

export function TaskTypeViewPlacementRow({
  view,
  regionId,
  placement,
  index,
  count,
  fields,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  placement: TaskTypeViewPlacement;
  index: number;
  count: number;
  fields: readonly ViewSchemaField[];
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const gt = useGT();
  function commit(next: TaskTypeView) {
    if (next !== view) onCommit(next);
  }

  if (isTaskTypeViewSourcePlacement(placement)) {
    return (
      <TaskTypeViewSourceRow
        view={view}
        regionId={regionId}
        placement={placement}
        index={index}
        count={count}
        fields={fields}
        fieldsReady={fieldsReady}
        disabled={disabled}
        onCommit={onCommit}
      />
    );
  }

  if (placement.component === 'supplierLineMatch') {
    return (
      <SupplierLinePlacementRow
        view={view}
        regionId={regionId}
        placement={placement}
        index={index}
        count={count}
        fields={fields}
        fieldsReady={fieldsReady}
        disabled={disabled}
        onCommit={commit}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-16 text-xs font-medium text-muted-foreground">
          {placementKindLabel(gt, placement.component)}
        </span>
        <PlacementEditors
          options={
            fieldsReady
              ? listViewFieldOptions(fields, optionKind(placement.component))
              : []
          }
          path={placement.config.path}
          onPath={(path) =>
            commit(setViewPlacementPath(view, regionId, placement.id, path))
          }
          label={placement.config.label}
          onLabel={(label) =>
            commit(setViewPlacementLabel(view, regionId, placement.id, label))
          }
          disabled={disabled}
          fieldsReady={fieldsReady}
          canMoveUp={index > 0}
          canMoveDown={index < count - 1}
          removeLabel={gt('Remove {kind}', {
            context: 'task page builder, remove a placed item',
            kind: placementKindLabel(gt, placement.component),
          })}
          onMoveUp={() =>
            commit(moveViewPlacement(view, regionId, placement.id, -1))
          }
          onMoveDown={() =>
            commit(moveViewPlacement(view, regionId, placement.id, 1))
          }
          onRemove={() =>
            commit(removeViewPlacement(view, regionId, placement.id))
          }
        >
          {placement.component === 'match' ? (
            <ViewMatchTargetToggle
              target={placement.config.target}
              disabled={disabled}
              onChange={(target) =>
                commit(setViewMatchTarget(view, regionId, placement.id, target))
              }
            />
          ) : null}
          {placement.component === 'lineList' ? null : (
            <ViewModeToggle
              mode={placement.config.mode}
              disabled={disabled}
              onChange={(mode) =>
                commit(setViewPlacementMode(view, regionId, placement.id, mode))
              }
            />
          )}
        </PlacementEditors>
      </div>
      {placement.component === 'lineList' ? (
        <LineListColumns
          view={view}
          regionId={regionId}
          placement={placement}
          fields={fields}
          fieldsReady={fieldsReady}
          disabled={disabled}
          onCommit={commit}
        />
      ) : null}
    </div>
  );
}

function LineListColumns({
  view,
  regionId,
  placement,
  fields,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  placement: Extract<TaskTypeViewPlacement, { component: 'lineList' }>;
  fields: readonly ViewSchemaField[];
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const scalarOptions = columnChoices(
    fieldsReady,
    fields,
    placement.config.path,
    'scalar',
  );
  const matchOptions = columnChoices(
    fieldsReady,
    fields,
    placement.config.path,
    'match',
  );

  return (
    <div className="flex flex-col gap-2 border-l pl-3">
      <ColumnStack
        view={view}
        regionId={regionId}
        placement={placement}
        scalarOptions={scalarOptions}
        matchOptions={matchOptions}
        fieldsReady={fieldsReady}
        disabled={disabled}
        onCommit={onCommit}
      />
      <ColumnAddButtons
        view={view}
        regionId={regionId}
        placement={placement}
        fields={fields}
        scalarCount={scalarOptions.length}
        matchCount={matchOptions.length}
        fieldsReady={fieldsReady}
        disabled={disabled}
        onCommit={onCommit}
      />
    </div>
  );
}

function columnChoices(
  fieldsReady: boolean,
  fields: readonly ViewSchemaField[],
  path: readonly string[],
  kind: 'scalar' | 'match',
): ReturnType<typeof listColumnOptions> {
  if (!fieldsReady) return [];
  return listColumnOptions(fields, path, kind);
}

function ColumnStack({
  view,
  regionId,
  placement,
  scalarOptions,
  matchOptions,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  placement: Extract<TaskTypeViewPlacement, { component: 'lineList' }>;
  scalarOptions: ReturnType<typeof listColumnOptions>;
  matchOptions: ReturnType<typeof listColumnOptions>;
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  if (placement.config.columns.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        <T context="task page builder empty line list columns">
          No columns yet. Each column is a field on one row.
        </T>
      </p>
    );
  }
  return (
    <>
      {placement.config.columns.map((column, index) => (
        <ColumnRow
          key={column.id}
          view={view}
          regionId={regionId}
          lineListId={placement.id}
          column={column}
          index={index}
          count={placement.config.columns.length}
          options={column.component === 'match' ? matchOptions : scalarOptions}
          fieldsReady={fieldsReady}
          disabled={disabled}
          onCommit={onCommit}
        />
      ))}
    </>
  );
}

function ColumnAddButtons({
  view,
  regionId,
  placement,
  fields,
  scalarCount,
  matchCount,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  placement: Extract<TaskTypeViewPlacement, { component: 'lineList' }>;
  fields: readonly ViewSchemaField[];
  scalarCount: number;
  matchCount: number;
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const gt = useGT();
  const atCap = placement.config.columns.length >= MAX_VIEW_COLUMNS;
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <AddColumnButton
          disabled={columnAddDisabled({
            disabled,
            fieldsReady,
            atCap,
            optionCount: scalarCount,
          })}
          onClick={() =>
            commitColumn(
              view,
              regionId,
              placement.id,
              'field',
              fields,
              onCommit,
            )
          }
        >
          <T context="task page builder, add a column that shows a row field">
            Add Column
          </T>
        </AddColumnButton>
        <AddColumnButton
          disabled={columnAddDisabled({
            disabled,
            fieldsReady,
            atCap,
            optionCount: matchCount,
          })}
          onClick={() =>
            commitColumn(
              view,
              regionId,
              placement.id,
              'match',
              fields,
              onCommit,
            )
          }
        >
          <T context="task page builder, add a column with a supplier or product typeahead that stores an id">
            Add Id Picker Column
          </T>
        </AddColumnButton>
      </div>
      {showNoRowFields(fieldsReady, scalarCount, matchCount) ? (
        <p className="text-xs text-muted-foreground">
          {gt('This list has no row fields on the input schema.', {
            context: 'task page builder when a line list cannot have columns',
          })}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          <T context="task page builder, what a line list column and an id picker column are">
            A column shows one row field. An id picker column is a supplier or
            product typeahead that writes the chosen id into a string row field.
            For extracted document lines with a match status, use Supplier Lines
            instead.
          </T>
        </p>
      )}
    </>
  );
}

function AddColumnButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function commitColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  component: 'field' | 'match',
  fields: readonly ViewSchemaField[],
  onCommit: (next: TaskTypeView) => void,
): void {
  const next = addViewColumn(view, regionId, placementId, component, fields);
  if (next) onCommit(next);
}

function columnAddDisabled(input: {
  disabled?: boolean;
  fieldsReady: boolean;
  atCap: boolean;
  optionCount: number;
}): boolean {
  if (input.disabled || !input.fieldsReady || input.atCap) return true;
  return input.optionCount === 0;
}

function showNoRowFields(
  fieldsReady: boolean,
  scalarCount: number,
  matchCount: number,
): boolean {
  return fieldsReady && scalarCount === 0 && matchCount === 0;
}

function ColumnRow({
  view,
  regionId,
  lineListId,
  column,
  index,
  count,
  options,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  lineListId: string;
  column: Extract<
    TaskTypeViewPlacement,
    { component: 'lineList' }
  >['config']['columns'][number];
  index: number;
  count: number;
  options: ReturnType<typeof listColumnOptions>;
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const gt = useGT();
  function commit(next: TaskTypeView) {
    if (next !== view) onCommit(next);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-16 text-xs text-muted-foreground">
        {placementKindLabel(gt, column.component)}
      </span>
      <ViewPathSelect
        options={options}
        path={column.config.path}
        disabled={disabled || !fieldsReady}
        onChange={(path) =>
          commit(setViewColumnPath(view, regionId, lineListId, column.id, path))
        }
      />
      {column.component === 'match' ? (
        <ViewMatchTargetToggle
          target={column.config.target}
          disabled={disabled}
          onChange={(target) =>
            commit(
              setViewColumnTarget(
                view,
                regionId,
                lineListId,
                column.id,
                target,
              ),
            )
          }
        />
      ) : null}
      <ViewModeToggle
        mode={column.config.mode}
        disabled={disabled}
        onChange={(mode) =>
          commit(setViewColumnMode(view, regionId, lineListId, column.id, mode))
        }
      />
      <ViewLabelInput
        label={column.config.label}
        disabled={disabled}
        onChange={(label) =>
          commit(
            setViewColumnLabel(view, regionId, lineListId, column.id, label),
          )
        }
      />
      <RowMoves
        disabled={disabled}
        canMoveUp={index > 0}
        canMoveDown={index < count - 1}
        removeLabel={gt('Remove column', {
          context: 'task page builder, remove a line list column',
        })}
        onMoveUp={() =>
          commit(moveViewColumn(view, regionId, lineListId, column.id, -1))
        }
        onMoveDown={() =>
          commit(moveViewColumn(view, regionId, lineListId, column.id, 1))
        }
        onRemove={() =>
          commit(removeViewColumn(view, regionId, lineListId, column.id))
        }
      />
    </div>
  );
}

function SupplierLinePlacementRow({
  view,
  regionId,
  placement,
  index,
  count,
  fields,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  placement: Extract<TaskTypeViewPlacement, { component: 'supplierLineMatch' }>;
  index: number;
  count: number;
  fields: readonly ViewSchemaField[];
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-16 text-xs font-medium text-muted-foreground">
          <T context="task page builder item kind, supplier document lines">
            Lines
          </T>
        </span>
        <PlacementEditors
          options={fieldsReady ? listSupplierLineOptions(fields) : []}
          path={placement.config.path}
          onPath={(path) =>
            onCommit(setViewPlacementPath(view, regionId, placement.id, path))
          }
          label={placement.config.label}
          onLabel={(label) =>
            onCommit(setViewPlacementLabel(view, regionId, placement.id, label))
          }
          disabled={disabled}
          fieldsReady={fieldsReady}
          canMoveUp={index > 0}
          canMoveDown={index < count - 1}
          removeLabel={gt('Remove {kind}', {
            context: 'task page builder, remove a placed item',
            kind: gt('Lines', {
              context: 'task page builder item kind, supplier document lines',
            }),
          })}
          onMoveUp={() =>
            onCommit(moveViewPlacement(view, regionId, placement.id, -1))
          }
          onMoveDown={() =>
            onCommit(moveViewPlacement(view, regionId, placement.id, 1))
          }
          onRemove={() =>
            onCommit(removeViewPlacement(view, regionId, placement.id))
          }
        >
          <ViewSupplierPathSelect
            options={fieldsReady ? listViewSupplierOptions(fields) : []}
            path={placement.config.supplierPath}
            disabled={disabled || !fieldsReady}
            onChange={(path) =>
              onCommit(
                setSupplierLineSupplierPath(view, regionId, placement.id, path),
              )
            }
          />
        </PlacementEditors>
      </div>
      <TaskTypeViewSupplierLineColumns
        view={view}
        regionId={regionId}
        placement={placement}
        disabled={disabled}
        onCommit={onCommit}
      />
    </div>
  );
}

function PlacementEditors({
  options,
  path,
  onPath,
  label,
  onLabel,
  disabled,
  fieldsReady,
  canMoveUp,
  canMoveDown,
  removeLabel,
  onMoveUp,
  onMoveDown,
  onRemove,
  children,
}: {
  options: readonly ViewFieldOption[];
  path: string[];
  onPath: (path: string[]) => void;
  label: string | null;
  onLabel: (label: string) => void;
  disabled?: boolean;
  fieldsReady: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  removeLabel: string;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  children?: ReactNode;
}) {
  return (
    <>
      <ViewPathSelect
        options={fieldsReady ? options : []}
        path={path}
        disabled={disabled || !fieldsReady}
        onChange={onPath}
      />
      {children}
      <ViewLabelInput label={label} disabled={disabled} onChange={onLabel} />
      <RowMoves
        disabled={disabled}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        removeLabel={removeLabel}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onRemove={onRemove}
      />
    </>
  );
}

function RowMoves({
  disabled,
  canMoveUp,
  canMoveDown,
  removeLabel,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  disabled?: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  removeLabel: string;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  return (
    <div className="ml-auto flex items-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={disabled || !canMoveUp}
        aria-label={gt('Move up', {
          context: 'task page builder, move an item up',
        })}
        onClick={onMoveUp}
      >
        <ArrowUpIcon />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={disabled || !canMoveDown}
        aria-label={gt('Move down', {
          context: 'task page builder, move an item down',
        })}
        onClick={onMoveDown}
      >
        <ArrowDownIcon />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={disabled}
        aria-label={removeLabel}
        onClick={onRemove}
      >
        <TrashIcon />
      </Button>
    </div>
  );
}

function optionKind(
  component: 'field' | 'match' | 'lineList',
): 'scalar' | 'match' | 'lineList' {
  if (component === 'field') return 'scalar';
  if (component === 'match') return 'match';
  return 'lineList';
}

function placementKindLabel(
  gt: ReturnType<typeof useGT>,
  component: 'field' | 'match' | 'lineList',
): string {
  if (component === 'field') {
    return gt('Field', { context: 'task page builder item kind' });
  }
  if (component === 'match') {
    return gt('Match', { context: 'task page builder item kind' });
  }
  return gt('Lines', { context: 'task page builder item kind, a line list' });
}
